import { FC, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  GlobalStyles,
  alpha,
  lighten,
  useTheme,
  Snackbar,
  Alert as MuiAlert,
  Typography
} from '@mui/material';

import Sidebar from './Sidebar';
import Header from './Header';
import Footer from '@/components/Footer';
import TourFAB from '@/components/TourFAB';
import ChangelogFAB from '@/components/ChangelogFAB';
import ChangelogDrawer from '@/components/ChangelogDrawer';
import { ChangelogProvider } from '@/contexts/ChangelogContext';
import SubscriptionBanner from '@/components/SubscriptionBanner';
import { SmsQuotaBanner } from '@/components/SmsQuotaBanner';
import { NotificationPermissionBanner } from '@/components/NotificationPermissionBanner';
import { API_ERROR_EVENT } from '@/data/apiService';
import { useAdminPush } from '@/hooks/useAdminPush';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import CommandPalette from '@/components/CommandPalette';
import { useStackedTables } from '@/hooks/useStackedTables';
import { stackedTableStyles } from './stackedTableStyles';

/**
 * Phones and tablets (below `md`): pages are written for desktop (`<Grid container
 * spacing={3}>`, card headers with a row of filters and buttons). Rather
 * than touching every page, the content area adapts them here:
 * - a small page gutter, and grids use an 8 px gap whose negative margin
 *   that gutter absorbs (spacing 3 = 27 px pushed content off the left edge);
 * - card headers wrap: title on top, actions on their own full-width rows;
 * - tables scroll sideways inside their card instead of widening the page
 *   (on phones, one too wide for its card is shown as cards instead:
 *   `useStackedTables`);
 * - tab bars scroll instead of overflowing.
 */
const PHONE = '@media (max-width: 599.95px)';
// The theme's `md` is 960 px.
const COMPACT = '@media (max-width: 959.95px)';
const phoneLayout = {
  [COMPACT]: {
    px: 1,
    '& .MuiGrid-container.MuiGrid-container': {
      marginLeft: '-8px',
      width: 'calc(100% + 8px)'
    },
    '& .MuiGrid-container.MuiGrid-container > .MuiGrid-item': {
      paddingLeft: '8px'
    },
    '& .MuiCardHeader-root': {
      flexWrap: 'wrap',
      rowGap: 1,
      px: 1.5
    },
    '& .MuiCardHeader-action': {
      m: 0,
      flex: '1 1 100%',
      minWidth: 0,
      alignSelf: 'stretch'
    },
    '& .MuiCardHeader-action > .MuiBox-root, & .MuiCardHeader-action .MuiStack-root':
      {
        flexWrap: 'wrap',
        rowGap: 1,
        columnGap: 1,
        width: '100%',
        justifyContent: 'flex-start'
      },
    // Desktop spacing between header buttons; the wrap gap replaces it.
    '& .MuiCardHeader-action .MuiButton-root': {
      ml: 0
    },
    '& .MuiCardHeader-action .MuiTextField-root, & .MuiCardHeader-action .MuiFormControl-root':
      {
        flex: '1 1 160px',
        minWidth: 0,
        maxWidth: '100%'
      },
    '& .MuiTableContainer-root': {
      overflowX: 'auto'
    },
    // `clip`, not `hidden`: a hidden card is still a scroll box, and
    // focusing a control past its edge scrolled the whole card sideways.
    '& .MuiCard-root': {
      maxWidth: '100%',
      overflow: 'clip'
    },
    '& .MuiTabs-root:not(.MuiTabs-vertical) .MuiTabs-scroller': {
      overflowX: 'auto !important'
    },
    // A tab bar inside a flex row must be allowed to shrink.
    '& .MuiTabs-root': {
      minWidth: 0,
      maxWidth: '100%'
    },
    // Wizards (e.g. new notification): the step bar scrolls sideways.
    '& .MuiStepper-horizontal': {
      overflowX: 'auto',
      pb: 1
    },
    '& .MuiStepper-horizontal .MuiStep-root': {
      flexShrink: 0
    },
    '& img': {
      maxWidth: '100%'
    },
    '& .MuiTablePagination-toolbar': {
      flexWrap: 'wrap',
      px: 1
    }
  }
} as const;

/**
 * Dialogs render in a portal, outside the content area: on a phone they
 * take the screen with a small margin instead of MUI's 32 px (which left
 * forms 300 px wide), and their action rows wrap.
 */
const phoneDialogs = {
  [PHONE]: {
    '.MuiDialog-paper.MuiDialog-paper': {
      margin: 8,
      width: 'calc(100% - 16px)',
      maxWidth: 'calc(100% - 16px)',
      maxHeight: 'calc(100% - 16px)'
    },
    '.MuiDialog-paper.MuiDialog-paperFullScreen': {
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      maxHeight: '100%'
    },
    '.MuiDialogContent-root': {
      paddingLeft: 12,
      paddingRight: 12
    },
    '.MuiDialogActions-root': {
      flexWrap: 'wrap',
      gap: 8
    },
    '.MuiDialogActions-root > :not(style) ~ :not(style)': {
      marginLeft: 0
    }
  }
};

const SidebarLayout: FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { needsPermission, enablePush } = useAdminPush();
  // Phones: tables that would scroll sideways are shown as cards.
  useStackedTables();

  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<
    'error' | 'warning' | 'info' | 'success'
  >('error');

  // Listen for API error events
  useEffect(() => {
    const handleApiError = (event: Event) => {
      const customEvent = event as CustomEvent<{
        message: string;
        severity: string;
      }>;
      setAlertSeverity(
        customEvent.detail.severity as 'error' | 'warning' | 'info' | 'success'
      );
      setErrorMessage(customEvent.detail.message);
      setShowError(true);
    };

    window.addEventListener(API_ERROR_EVENT, handleApiError);

    return () => {
      window.removeEventListener(API_ERROR_EVENT, handleApiError);
    };
  }, []);

  return (
    <ChangelogProvider>
      <Box
        sx={{
          flex: 1,
          height: '100%',
          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                    lighten(theme.colors.primary.main, 0.7),
                    0.15
                  )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                    theme.colors.alpha.black[100],
                    0.1
                  )}, 0px 5px 12px -4px ${alpha(
                    theme.colors.alpha.black[100],
                    0.05
                  )}`
          }
        }}
      >
        <Header />
        <Sidebar />
        <Box
          component="main"
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            pt: `${theme.header.height}`,
            width: {
              xs: '100%',
              lg: `calc(100% - ${theme.sidebar.width})`
            },
            marginLeft: { xs: 0, lg: theme.sidebar.width },
            minHeight: '100vh'
          }}
        >
          <SubscriptionBanner />
          <SmsQuotaBanner />
          <NotificationPermissionBanner
            visible={needsPermission}
            onEnable={enablePush}
          />
          <Box sx={{ flex: 1, ...phoneLayout }}>
            <RouteErrorBoundary key={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </Box>
          <Footer />
        </Box>
      </Box>

      <GlobalStyles styles={phoneDialogs} />
      <GlobalStyles styles={stackedTableStyles} />
      <CommandPalette />
      <TourFAB />
      <ChangelogFAB />
      <ChangelogDrawer />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <MuiAlert
          variant="filled"
          severity={alertSeverity}
          onClose={() => setShowError(false)}
        >
          <Typography>{errorMessage}</Typography>
        </MuiAlert>
      </Snackbar>
    </ChangelogProvider>
  );
};

export default SidebarLayout;
